import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApolloError, useApolloClient } from '@apollo/client'
import { equals } from 'ramda'
import useDeepCompareEffect from 'use-deep-compare-effect'

import { ALL_TIME_RANGE_QUERY } from 'consts/filterOptions'
import type { Get_Territory_For_TableQuery, Get_Territory_For_TableQueryVariables } from 'generated/graphql'
import { GET_TERRITORY_FOR_TABLE } from 'graphql/territory/operations'
import { useElectric } from 'hooks/use-electric'
import type { NicheElectricType, NicheStatusElectricType } from 'hooks/use-electric/types'
import { useTranslate } from 'lib/tolgee'
import { resolvePromotedNicheTargetId, resolvePromotedNicheTargetName } from 'modules/niche/common/promoted-niche.utils'
import { PanelStore } from 'stores'
import { getTerritorySubtitleForTable } from 'utils/getTerritorySubtitleForTable'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { getFormatedDateVariablesForTerritory, getTerritoryLinkByType } from 'utils/territoryUtils'

import { GET_TERRITORY_FOR_TABLE_LOG } from './use-niche-with-context.consts'
import type { IUseNicheWithContext, NichesWithData } from './use-niche-with-context.types'
import { resolveParentTerritoryIds } from './use-niche-with-context.utils'

export const useNicheWithContext = ({
  providers,
  uploadDateParam,
  contextHash,
  shouldGetPosts,
  parentTerritoryIdsOverride,
  territoryContentFilters,
  contentFilters,
}: IUseNicheWithContext) => {
  const [nichesWithData, setNichesWithData] = useState<NichesWithData>({})
  const [nicheState, setNicheState] = useState([])
  // Bumps on every contextHash change. Async fetches/effects capture this epoch at
  // start and discard their result if the epoch has advanced — prevents data from a
  // prior chip from leaking into the current chip's state when network is unstable.
  const epochRef = useRef(0)

  const {
    data: nicheStatus,
    error: errorStatus,
    handleRefetch: refetchStatus,
    unsubscribe: unsubscribeStatus,
  } = useElectric<NicheStatusElectricType>({
    table: 'generated_niche',
    skip: !contextHash,
    variables: `id = '${contextHash}' AND deleted_at IS NULL`,
  })

  useEffect(() => {
    epochRef.current += 1
    setNicheState([])
    setNichesWithData({})
  }, [contextHash])

  const t = useTranslate()

  const generatedNicheId = useMemo(() => {
    const currentNicheId = nicheStatus?.[0]?.id
    return currentNicheId
  }, [nicheStatus])

  const generatedNicheStatus = useMemo(() => {
    const status = nicheStatus?.[0]?.status
    return status
  }, [nicheStatus])

  const generatedNicheDescription = useMemo(() => {
    const nicheContext = nicheStatus?.[0]?.generation_context
    if (!nicheContext) {
      return ''
    }
    return nicheContext?.contextual_input?.context_interpretation || ''
  }, [nicheStatus])

  const {
    data: nicheData,
    isInitialized,
    error: errorNicheList,
    handleRefetch: refetchNicheList,
    unsubscribe: unsubscribeNicheList,
  } = useElectric<NicheElectricType>({
    table: 'niche',
    variables: `generated_niche_id = '${generatedNicheId}' AND deleted_at IS NULL`,
    skip: !generatedNicheId,
  })

  const handleRefetch = useCallback(() => {
    if (errorStatus || generatedNicheStatus === 'error') {
      refetchStatus()
      return
    }
    if (errorNicheList) {
      refetchNicheList()
      return
    }
  }, [errorNicheList, errorStatus, generatedNicheStatus, refetchNicheList, refetchStatus])

  // Use the Apollo client directly instead of useLazyQuery because rapid concurrent
  // loadQuery() calls share the hook's observable — all promises end up resolving with
  // the LAST call's data instead of each call's own data, which previously caused 40+
  // niches to display data from a single niche.
  const apolloClient = useApolloClient()

  useEffect(() => {
    setNicheState(nicheData)
  }, [nicheData])

  const nichesList = useMemo<Array<{ name: string; territoryId: string }>>(() => {
    return nicheState
      .filter(item => Boolean(item?.id))
      .map(item => ({
        name: item.name,
        territoryId: item.id.includes('topic') ? item.id : `niche.${item.id}`,
      }))
  }, [nicheState])

  const getQueryVariables = useCallback(() => {
    const dateValue = getFormatedDateVariablesForTerritory(uploadDateParam)
    const isCustomDate = uploadDateParam?.includes('custom')
    const isAllTime = uploadDateParam === 'all'

    const shouldUseTimeRange = !isCustomDate && !isAllTime
    const publishedAtValue = isCustomDate ? getUploadDateByValue(uploadDateParam) : ALL_TIME_RANGE_QUERY

    return {
      providers,
      shouldGetPosts: Boolean(shouldGetPosts),
      ...(shouldUseTimeRange
        ? {
            timeRange: {
              timeUnit: dateValue?.timeUnit,
              timeValue: dateValue?.timeValue,
            },
          }
        : {
            publishedAt: publishedAtValue,
            timeRange: null,
          }),
      selfContentBrandIds: contentFilters?.selfContentBrandIds,
      paidBrandIds: contentFilters?.paidBrandIds,
      earnedBrandIds: contentFilters?.earnedBrandIds,
      territoryIds: territoryContentFilters?.territoryIds,
    }
  }, [providers, shouldGetPosts, uploadDateParam, contentFilters, territoryContentFilters])

  const unsubscribeAll = useCallback(() => {
    unsubscribeNicheList()
    unsubscribeStatus()
  }, [unsubscribeNicheList, unsubscribeStatus])

  useDeepCompareEffect(() => {
    const arr = nichesList.map(item => {
      const baseItem = {
        isLoading: false,
        id: item.territoryId,
        data: null,
        name: item.name,
        providers,
        uploadDateParam,
        error: null,
        retryCount: 0,
      }
      return baseItem
    })
    const newState = arr.reduce((acc, item) => {
      const currentItem = arr.find(arrayItem => arrayItem.id === item.id)

      const mergedItem = {
        ...item,
        ...currentItem,
      }
      return {
        ...acc,
        [item.id]: mergedItem,
      }
    }, {})

    setNichesWithData(newState)
  }, [nichesList, providers, uploadDateParam, contentFilters])

  const handleFetchTerritory = useCallback(
    async (territoryId: string, isRetry?: boolean) => {
      const myEpoch = epochRef.current
      const baseQueryVariables = getQueryVariables()
      let data: Get_Territory_For_TableQuery | undefined
      let error: ApolloError | undefined
      try {
        const result = await apolloClient.query<Get_Territory_For_TableQuery, Get_Territory_For_TableQueryVariables>({
          query: GET_TERRITORY_FOR_TABLE,
          variables: {
            territoryId: territoryId,
            shouldGetPosts: Boolean(shouldGetPosts),
            ...baseQueryVariables,
          } as Get_Territory_For_TableQueryVariables,
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
          context: { errorLog: GET_TERRITORY_FOR_TABLE_LOG },
        })
        data = result.data
        error = result.error
      } catch (e) {
        error = e instanceof ApolloError ? e : new ApolloError({ networkError: e as Error })
      }

      // Discard the result if the active context changed while we were awaiting —
      // otherwise stale per-niche data from the prior chip would leak into the new state.
      if (myEpoch !== epochRef.current) return

      if (error) {
        setNichesWithData(prevState => {
          const currentItem = prevState[territoryId]
          // The territoryId may have been wiped from state by a context switch in between.
          if (!currentItem) return prevState
          return {
            ...prevState,
            [territoryId]: {
              ...currentItem,
              isLoading: false,
              error: error,
              ...(isRetry && { retryCount: 1 }),
            },
          }
        })
        return
      }

      if (data?.getTerritory) {
        setNichesWithData(prevState => {
          const currentItem = prevState[territoryId]
          if (!currentItem) return prevState
          return {
            ...prevState,
            [territoryId]: {
              ...currentItem,
              isLoading: false,
              data: data?.getTerritory,
              error: null,
            },
          }
        })
      }
    },
    [apolloClient, getQueryVariables, shouldGetPosts]
  )

  useDeepCompareEffect(() => {
    setNichesWithData(prevState => {
      const arrayKeys = Object.keys(prevState)
      const newState = arrayKeys.reduce((prev, curr) => {
        const currentObject = prevState[curr]
        const hasAlreadyRetried = currentObject.error && currentObject.retryCount > 0
        if (currentObject.isLoading || hasAlreadyRetried) {
          return {
            ...prev,
            [curr]: {
              ...currentObject,
            },
          }
        }

        if (currentObject.error && currentObject.retryCount === 0) {
          handleFetchTerritory(curr, true)
          return {
            ...prevState,
            [curr]: {
              ...currentObject,
              isLoading: true,
              retryCount: 1,
            },
          }
        }

        if (currentObject.error || currentObject.retryCount >= 1) {
          return {
            ...prevState,
            [curr]: {
              ...currentObject,
              isLoading: false,
              data: null,
            },
          }
        }

        const didNotFetch = !currentObject.isLoading && !currentObject.data
        if (didNotFetch) {
          handleFetchTerritory(curr)
          return {
            ...prev,
            [curr]: {
              ...currentObject,
              isLoading: true,
              retryCount: 1,
            },
          }
        }
        const hasChangedUploadDate = currentObject.uploadDateParam !== uploadDateParam
        const hasChangedProviders = !equals(currentObject?.providers, providers)
        if (hasChangedUploadDate || hasChangedProviders) {
          handleFetchTerritory(curr)
          return {
            ...prev,
            [curr]: {
              ...currentObject,
              uploadDateParam,
              providers,
              isLoading: true,
            },
          }
        }
        return {
          ...prev,
          [curr]: {
            ...currentObject,
          },
        }
      }, prevState)
      return newState
    })
  }, [handleFetchTerritory, nichesList, providers, uploadDateParam, contentFilters])

  const niches = useMemo(() => {
    const workspaceId = (PanelStore?.selectedPanel as any)?.id
    const nichesKeys = Object.keys(nichesWithData)
    const nichesArray = nichesKeys.map(item => nichesWithData[item])

    const validNiches = nichesArray.filter(item => {
      const hasError = !!item.error
      const hasRetried = item.retryCount > 0
      const isNotLoading = !item.isLoading
      if (hasError && hasRetried && isNotLoading) {
        return false
      }
      return item?.data?.territoryPerformance?.totalPosts !== 0
    })

    const formattedNiches = validNiches.map(item => {
      const data = item?.data
      const promotedTargetId = resolvePromotedNicheTargetId(data)
      const promotedTargetName = resolvePromotedNicheTargetName(data)
      const parents = getTerritorySubtitleForTable(data, t)
      const breadcrumbItems = parents.map(item => item?.name)?.filter(item => item)
      const territoryId = item?.id || data?.territoryId
      const [entityType, entityId] = territoryId?.split('.') ?? []
      const url = entityId ? getTerritoryLinkByType(entityType || 'niche', entityId, workspaceId) : '#'
      const parentTerritoryIds = resolveParentTerritoryIds(data, parentTerritoryIdsOverride)
      return {
        isLoading: item.isLoading,
        name: promotedTargetName || data?.name || item?.name,
        posts: data?.territoryMostEngaged?.posts || [],
        isFollowed: data?.isFollowed,
        breadcrumbItems,
        totalPosts: data?.territoryPerformance?.totalPosts ?? -1,
        avgEngagement: data?.territoryPerformance?.sampledMetrics?.avgEngagement ?? -1,
        totalEngagement: data?.territoryPerformance?.metrics?.totalEngagement ?? -1,
        totalViews: data?.territoryPerformance?.metrics?.totalViews ?? -1,
        avgViews: data?.territoryPerformance?.sampledMetrics?.avgViews ?? -1,
        territoryId: data?.territoryId || item?.id,
        id: data?.territoryId || item?.id,
        href: url,
        dateRange: uploadDateParam,
        audience: '',
        highlight: '',
        territoryQuery: data?.__typename === 'Niche' ? data?.territoryQuery : undefined,
        parentTerritoryIds,
        isPromoted: Boolean(promotedTargetId),
        promotedTargetId: promotedTargetId ?? null,
      }
    })
    return formattedNiches
  }, [nichesWithData, t, uploadDateParam, parentTerritoryIdsOverride])

  const allNichesHaveErrors = useMemo(() => {
    const nichesKeys = Object.keys(nichesWithData)
    const nichesArray = nichesKeys.map(item => nichesWithData[item])
    return nichesArray.every(item => item.error) && nichesArray.length > 0
  }, [nichesWithData])

  const hasError = useMemo(() => {
    return Boolean(errorStatus || errorNicheList)
  }, [errorNicheList, errorStatus])

  return {
    niches,
    unsubscribe: unsubscribeAll,
    nicheStatus: generatedNicheStatus,
    handleRefetch,
    hasError,
    isNicheListInitialized: isInitialized,
    allNichesHaveErrors,
    generatedNicheDescription,
  }
}
